import React, { useEffect, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "../styles/PropertiesPanel.css";
import API from "../api/axiosInstance";
import {
    Box, Typography, TextField, Button, ToggleButton, FormControlLabel, Switch,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { useTheme } from "@mui/material/styles";
import { TableField, PermissionRow, Table } from "../types/interfaces";
import { sanitizeText } from "../utils/validators";
import FieldOptionsDialog from './FieldOptionsDialog';

interface Props {
    table: Table;
    setTable: React.Dispatch<React.SetStateAction<Table>>;
    setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
    onValidationError: (hasError: boolean) => void;
}

const PropertiesPanel: React.FC<Props> = ({ table, setTable, setIsDirty, onValidationError }) => {
    const theme = useTheme();
    const nameInputRef = useRef<HTMLInputElement | null>(null);

    const [description, setDescription] = useState<string>(table.description || "");
    const [showFieldError, setShowFieldError] = useState(false);
    const [selectedFieldForOptions, setSelectedFieldForOptions] = useState<TableField | null>(null);
    const [fields, setFields] = useState<TableField[]>(table.fields || []);
    const [permissions, setPermissions] = useState<PermissionRow[]>([]);
    const [showUnpublishDialog, setShowUnpublishDialog] = useState(false);
    const [pendingUnpublish, setPendingUnpublish] = useState(false);


    // Fetch permissions
    useEffect(() => {
        if (!table.tableID) return;
        fetchPermissions(table.tableID);
    }, [table.tableID]);

    const fetchPermissions = async (tableId: string) => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const headers = {
                headers: {
                    authToken: user.user.authtoken,
                    sessionId: user.user.sessionId
                }
            };

            //const response = await API.get(`/tables/${tableId}/permissions`,headers);
            //setPermissions(response.data || []);
        } catch (error) {
            console.error("Error fetching permissions:", error);
        }
    };

    // Sync fields to table
    const updateFields = (newFields: TableField[]) => {
        setFields(newFields);
        setTable(prev => ({ ...prev, fields: newFields }));
        setIsDirty(true);
    };

    const generateGUID = (): string => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    };


    const handleAddField = () => {
        const newField: TableField = {
            tableFieldID: generateGUID(),
            fieldID: generateGUID(),
            tableID: table.tableID,
            fieldName: "Field " + (fields.length+1).toString(),
            dataType: "Text",
            required: false,
            readOnly: false,
            displayOrder: fields.length,
            format: "",
            inputMask: "",
            allowSpecialCharacters: false,
            isEncrypted: false,
            isBlob: false,
            valueTable: "FieldValueA",
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            listValues: ""
        };
        updateFields([...fields, newField]);
    };

    const handleDeleteField = (params: ICellRendererParams<TableField>) => {
        updateFields(fields.filter(field => field.fieldID !== params.data!.fieldID));
    };

    const toggleFieldRequired = (params: ICellRendererParams<TableField>) => {
        updateFields(
            fields.map(field =>
                field.fieldID === params.data!.fieldID ? { ...field, required: !field.required } : field
            )
        );
    };

    const toggleFieldReadOnly = (params: ICellRendererParams<TableField>) => {
        updateFields(
            fields.map(field =>
                field.fieldID === params.data!.fieldID ? { ...field, readOnly: !field.readOnly } : field
            )
        );
    };

    const handleFieldOptions = (params: ICellRendererParams<TableField>) => {
        if (params.data) {
            setSelectedFieldForOptions(params.data);
        }
    };

    const handleAddPermission = () => {
        const newPermission: PermissionRow = {
            id: Date.now(),
            group: "",
            canAdd: false,
            canEdit: false,
            canView: false,
            canDelete: false,
        };
        setPermissions(prev => [...prev, newPermission]);
        setIsDirty(true);
    };

    const handleDeletePermission = (params: ICellRendererParams<PermissionRow>) => {
        if (!params.data) return;
        const updated = permissions.filter(p => p.id !== params.data!.id);
        setPermissions(updated);
        setIsDirty(true);
    };

    const togglePermission = (params: ICellRendererParams<PermissionRow>, type: keyof PermissionRow) => {
        const updated = permissions.map(p =>
            p.id === params.data!.id ? { ...p, [type]: !p[type] } : p
        );
        setPermissions(updated);
        setIsDirty(true);
    };

    // Validation
    useEffect(() => {
        const hasError = !table.tableName || fields.length === 0;
        onValidationError(hasError);
        setShowFieldError(!table.tableName);
    }, [table.tableName, fields, onValidationError]);

    const fieldColumnDefs: ColDef<TableField>[] = [
        { field: "fieldName", headerName: "Field Name", editable: true, width: 180 },
        {
            field: "dataType",
            headerName: "Type",
            width: 150,
            editable: true,
            rowDrag: true,
            cellEditor: "agSelectCellEditor",
            cellEditorParams: {
                values: ["Text", "Number", "Boolean", "Date", "Time", "DateTime", "File", "Blob", "Dropdown"],
            },
        },
        {
            field: "required",
            headerName: "Required",
            width: 120,
            cellRenderer: (params: ICellRendererParams<TableField>) => (
                <ToggleButton
                    value="required"
                    selected={!params.data?.required}
                    onChange={() => toggleFieldRequired(params)}
                    size="small"
                    sx={{ fontSize: "0.75rem", minWidth: 28, height: 28 }}
                >
                    <CheckIcon fontSize="inherit" />
                </ToggleButton>

            ),
        },
        {
            field: "readOnly",
            headerName: "Read Only",
            width: 120,
            cellRenderer: (params: ICellRendererParams<TableField>) => (
                <ToggleButton
                    value="readOnly"
                    selected={!params.data?.readOnly}
                    onChange={() => toggleFieldReadOnly(params)}
                    sx={{ fontSize: "0.75rem", minWidth: 28, height: 28 }}
                    size="small"
                >
                    <CheckIcon fontSize="small" />
                </ToggleButton>

            ),
        },
        {
            colId: "options",
            headerName: "Options",
            width: 100,
            valueGetter: () => null,

            cellRenderer: (params: ICellRendererParams<TableField>) => (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleFieldOptions(params)}
                    sx={{
                        minWidth: 60,
                        padding: "2px 6px",
                        fontSize: "0.7rem",
                        lineHeight: 1,
                        color: theme.palette.mode === "dark" ? "#ccc" : "#333",
                        borderColor: theme.palette.divider,
                        "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                        },
                    }}
                >
                    Options
                </Button>

            ),
        },
        {
            colId: "delete",
            headerName: "Delete",
            width: 90,
            valueGetter: () => null,
            cellRenderer: (params: ICellRendererParams<TableField>) => (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleDeleteField(params)}
                    sx={{
                        color: "#ff9800",
                        borderColor: "#ff9800",
                        minWidth: 60,
                        padding: "2px 6px",
                        fontSize: "0.7rem",
                        lineHeight: 1,
                        "&:hover": {
                            backgroundColor: "rgba(255, 152, 0, 0.1)",
                            borderColor: "#fb8c00",
                        },
                    }}
                >
                    Delete
                </Button>
            ),
        },
    ];

    const sortedFields = [...fields].sort((a, b) => a.displayOrder - b.displayOrder);

    const permissionColumnDefs: ColDef<PermissionRow>[] = [
        {
            field: "group",
            headerName: "Group",
            editable: true,
            width: 200,
            cellEditor: "agSelectCellEditor",
            cellEditorParams: {
                values: ["Admin", "Editor", "Viewer"], // Replace with actual roles if different
            },
        },
        {
            field: "canView",
            headerName: "View",
            width: 80,
            cellRenderer: (params: ICellRendererParams<PermissionRow>) => (
                    <ToggleButton
                        value="canView"
                        selected={!!params.data?.canView}
                        onChange={() => togglePermission(params, "canView")}
                        size="small"
                        sx={{ fontSize: "0.75rem", minWidth: 28, height: 28 }}
                    >
                        <CheckIcon fontSize="inherit" />
                </ToggleButton>
            ),
        },
        {
            field: "canAdd",
            headerName: "Add",
            width: 80,
            cellRenderer: (params: ICellRendererParams<PermissionRow>) => (
                <ToggleButton
                    value="canAdd"
                    selected={!!params.data?.canAdd}
                    onChange={() => togglePermission(params, "canAdd")}
                    size="small"
                    sx={{ fontSize: "0.75rem", minWidth: 28, height: 28 }}
                >
                    <CheckIcon fontSize="inherit" />
                </ToggleButton>
            ),
        },
        {
            field: "canEdit",
            headerName: "Edit",
            width: 80,
            cellRenderer: (params: ICellRendererParams<PermissionRow>) => (
                <ToggleButton
                    value="canEdit"
                    selected={!!params.data?.canEdit}
                    onChange={() => togglePermission(params, "canEdit")}
                    size="small"
                    sx={{ fontSize: "0.75rem", minWidth: 28, height: 28 }}
                >
                    <CheckIcon fontSize="inherit" />
                </ToggleButton>
            ),
        },
        {
            field: "canDelete",
            headerName: "Delete",
            width: 80,
            cellRenderer: (params: ICellRendererParams<PermissionRow>) => (
                <ToggleButton
                    value="canDelete"
                    selected={!!params.data?.canDelete}
                    onChange={() => togglePermission(params, "canDelete")}
                    size="small"
                    sx={{ fontSize: "0.75rem", minWidth: 28, height: 28 }}
                >
                    <CheckIcon fontSize="inherit" />
                </ToggleButton>
            ),
        },
        {
            headerName: "Delete",
            width: 90,
            suppressMenu: true,
            sortable: false,
            cellRenderer: (params: ICellRendererParams<PermissionRow>) => (
                <Button
                    variant="outlined"
                    sx={{
                        minWidth: 60,
                        padding: "2px 6px",
                        fontSize: "0.7rem",
                        lineHeight: 1,
                        color: theme.palette.mode === "dark" ? "#ccc" : "#333",
                        borderColor: theme.palette.divider,
                        "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                        },
                    }}
                    size="small"
                    onClick={() => handleDeletePermissionRow(params)}
                >
                    Delete
                </Button>
            ),
        },
    ];

    const togglePublishedState = async () => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const headers = {
            headers: {
                authToken: user.user?.authtoken,
                sessionId: user.user?.sessionId,
            },
        };

        const tableId = table.tableID;
        const isCurrentlyPublished = table.published;

        const acquireLock = async () => {
            try {
                await API.post(`/locks/TABLE/${tableId}?lockType=STRUCTURE`, {}, headers);
                return true;
            } catch (err) {
                alert("Failed to acquire lock. Please try again.");
                return false;
            }
        };

        const releaseLock = async () => {
            try {
                await API.delete(`/locks/TABLE/${tableId}`, headers);
            } catch {
                // ignore
            }
        };

        const endpoint = isCurrentlyPublished
            ? `/tables/${tableId}/unpublish`
            : `/tables/${tableId}/publish`;

        const success = await acquireLock();
        if (!success) return;

        try {
            await API.post(endpoint, {}, headers);

            setTable(prev => ({
                ...prev,
                published: !isCurrentlyPublished,
            }));

            setIsDirty(true);
        } catch {
            alert("Failed to publish/unpublish the table.");
        } finally {
            await releaseLock();
            setShowUnpublishDialog(false);
        }
    };


    const handlePublishToggle = () => {
        if (table.published) {
            // Show warning before unpublishing
            setShowUnpublishDialog(true);
        } else {
            togglePublishedState();
        }
    };


    useEffect(() => {
        if (table.fields) {
            const sorted = [...table.fields].sort((a, b) => a.displayOrder - b.displayOrder);
            setFields(sorted);
        }
    }, [table.fields]);

    const handleDeletePermissionRow = (params: ICellRendererParams<PermissionRow>) => {
        const updated = permissions.filter(p => p !== params.data);
        setPermissions(updated);
        setTable(prev => ({ ...prev, permissions: updated }));
        setIsDirty(true);
    };


    return (
        <Box
            className="properties-panel"
            sx={{
                padding: 3,
                backgroundColor: theme.palette.background.default,
                color: theme.palette.text.primary,
                borderRadius: 2,
                marginBottom: 4,
            }}
        >
            <Typography variant="h6" gutterBottom>
                Table Properties
            </Typography>

            {table.isLocked && (
                <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                    ⚠️ This table is locked and cannot be modified.
                </Typography>
            )}

            <div className="properties-container">
                <div className="left-section">
                    <TextField
                        inputRef={nameInputRef}
                        label="Table Name"
                        fullWidth
                        value={table.tableName}
                        onChange={(e) => {
                            setTable({ ...table, tableName: e.target.value });
                            setIsDirty(true);
                        }}
                        error={showFieldError}
                        helperText={showFieldError ? "Table name is required." : ""}
                    />

                    <FormControlLabel
                        control={
                            <Switch
                                checked={table.published}
                                onChange={() => handlePublishToggle()}
                                disabled={table.isLocked} // Optional: prevent toggle if table is locked
                            />
                        }
                        label={table.published ? "Published" : "Unpublished"}
                        sx={{ mt: 2 }}
                    />

                    <TextField
                        label="Description"
                        fullWidth
                        multiline
                        rows={4}
                        value={description}
                        onChange={(e) => {
                            setDescription(e.target.value);
                            setTable({ ...table, description: e.target.value });
                            setIsDirty(true);
                        }}
                        sx={{ mt: 2 }}
                    />
                </div>

                <div className="right-section" style={{ width: "80%", position: "relative" }}>
                    {!selectedFieldForOptions && (
                        <>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="subtitle1">Fields</Typography>
                                <Button variant="contained" size="small" onClick={handleAddField}>
                                    + Add Field
                                </Button>
                            </Box>

                            <div
                                className={`ag-theme-${theme.palette.mode === "dark" ? "alpine-dark" : "alpine"}`}
                                style={{ height: 400, width: "100%" }}
                            >
                                {!fields || fields.length === 0 ? (
                                    <Box
                                        display="flex"
                                        justifyContent="center"
                                        alignItems="center"
                                        height={200}
                                        sx={{
                                            backgroundColor: theme.palette.background.paper,
                                            borderRadius: 2,
                                            border: `1px dashed ${theme.palette.divider}`,
                                            color: theme.palette.text.secondary,
                                        }}
                                    >
                                        <Typography variant="body2">No fields added yet. Click "Add Field" to get started.</Typography>
                                    </Box>
                                ) : (
                                    <AgGridReact
                                        columnDefs={fieldColumnDefs}
                                        rowData={sortedFields}
                                        rowDragManaged={true}
                                        rowSelection="single"
                                        onRowDragEnd={(event) => {
                                            const fromIndex = typeof event.node?.rowIndex === 'number' ? event.node.rowIndex : null;
                                            const toIndex = typeof event.overNode?.rowIndex === 'number' ? event.overNode.rowIndex : null;

                                            // Validate indexes
                                            if (fromIndex === null || toIndex === null || fromIndex === toIndex) return;

                                            // Make a copy and reorder
                                            const reordered = [...fields];
                                            const [movedItem] = reordered.splice(fromIndex, 1);
                                            reordered.splice(toIndex, 0, movedItem);

                                            // Update displayOrder
                                            const updatedFields = reordered.map((field, index) => ({
                                                ...field,
                                                displayOrder: index
                                            }));

                                            updateFields(updatedFields);
                                        }}




                                        animateRows
                                        suppressColumnVirtualisation={true}
                                        defaultColDef={{
                                            sortable: true,
                                            editable: true,
                                            resizable: true,
                                            autoHeight: true,
                                        }}
                                    />
                                )}
                            </div>
                        </>
                    )}

                </div>
            </div>
            <FieldOptionsDialog
                open={!!selectedFieldForOptions}
                field={selectedFieldForOptions}
                onClose={() => setSelectedFieldForOptions(null)}
                onSave={(updatedField) => {
                    const updatedFields = fields.map((f) =>
                    f.fieldID === updatedField.fieldID ? updatedField : f
                    );
                    updateFields(updatedFields);
                    setSelectedFieldForOptions(null);
                }}
                />




            <Box mt={5}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle1">Permissions</Typography>
                    <Button variant="contained" size="small" onClick={handleAddPermission}>
                        + Add Permission
                    </Button>
                </Box>

                {!permissions || permissions.length === 0 ? (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        height={200}
                        sx={{
                            backgroundColor: theme.palette.background.paper,
                            borderRadius: 2,
                            border: `1px dashed ${theme.palette.divider}`,
                            color: theme.palette.text.secondary,
                        }}
                    >
                        <Typography variant="body2">
                            No permissions defined. Click "Add Permission" to get started.
                        </Typography>
                    </Box>
                ) : (
                    <div
                        className={`ag-theme-${theme.palette.mode === "dark" ? "alpine-dark" : "alpine"}`}
                        style={{ height: 400, width: "100%" }}
                    >
                        <AgGridReact
                            columnDefs={permissionColumnDefs}
                            rowData={permissions}
                            rowSelection="single"
                            animateRows
                            defaultColDef={{
                                sortable: true,
                                editable: true,
                                resizable: true,
                                autoHeight: true,
                            }}
                        />
                    </div>
                )}
            </Box>

            <Dialog open={showUnpublishDialog} onClose={() => setShowUnpublishDialog(false)}>
                <DialogTitle>Unpublish Table</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to unpublish this table? Doing so will hide it and its records from all users.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowUnpublishDialog(false)}>Cancel</Button>
                    <Button
                        onClick={togglePublishedState}
                        variant="contained"
                        color="warning"
                    >
                        Yes, Unpublish
                    </Button>
                </DialogActions>
            </Dialog>


        </Box>
    );
};

export default PropertiesPanel;
